import type { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { HttpStatusCode } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { isResponseDomainErrorDto } from '@kerberos-compliance/compliance-shared-ui';
import type { Observable } from 'rxjs';
import { catchError, throwError } from 'rxjs';
import { MaintenanceService } from '../error-pages/maintenance-page/services/maintenance.service';
import { RedirectService } from '../services/redirect.service';
import { isWhitelistedEndpoint } from './utilities';

export const maintenanceInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next,
): Observable<HttpEvent<unknown>> => {
  // Asset and i18n requests can never put the API into maintenance mode, and they are issued
  // while the app is still bootstrapping — injecting anything here would risk a DI cycle.
  if (isWhitelistedEndpoint(request.url)) {
    return next(request);
  }

  // Services are resolved lazily in the error path, never during the request pass. Injecting
  // RedirectService eagerly re-enters a root injector that may still be hydrating it (NG0200).
  const injector = inject(Injector);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      // Check if the error status is 502, 503 or 504
      if (
        [HttpStatusCode.BadGateway, HttpStatusCode.ServiceUnavailable, HttpStatusCode.GatewayTimeout].includes(
          error.status,
        ) &&
        !isResponseDomainErrorDto(error.error)
      ) {
        injector.get(RedirectService).setRedirectUrl(injector.get(Router).url);

        injector.get(MaintenanceService).setMaintenanceMode(true);
      }

      return throwError(() => error);
    }),
  );
};
